"use client";

import type {
  BotIdTags,
  FormVariant,
  LeadFormSurface,
} from "@homeinstead/analytics";
import type { CareSeekerLeadFormResponse } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type { LeadFlowAnswers, LeadFlowOfficeSubmission } from "../types";

import {
  identifyLead,
  ServiceLeadFormSubmitEvent,
  track,
  trackLeadCreated,
} from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";
import { useEffect, useRef } from "react";
import { getSelectedCareTypes } from "../utils/get-selected-care-types";

const NO_ANSWERS: LeadFlowAnswers = {};

type LeadFlowResponse = LeadResponse<CareSeekerLeadFormResponse>;

type SubmissionResult = "duplicate" | "server-error" | "validation-error";

type UseSubmissionTrackingInput = {
  officeSubmission: LeadFlowOfficeSubmission;
  state: LeadFlowResponse | null;
  /** The answers as submitted, which outlive the store being cleared on success. */
  submittedAnswers: LeadFlowAnswers | null;
  surface: LeadFormSurface;
  versionId: string;
};

type SubmissionEventFields = {
  careFor: string;
  careOptions: string[];
  formVariant: FormVariant;
  formVersionId: string;
  organizationNumber: number;
  surface: LeadFormSurface;
};

function trackSubmission(
  fields: SubmissionEventFields,
  result: SubmissionResult | "success",
  tags: BotIdTags,
  validationErrors?: Record<string, string>,
  spamBlockReason?: string,
) {
  track(
    EventName.SERVICE_LEAD_FORM_SUBMIT,
    ServiceLeadFormSubmitEvent({
      formVariant: fields.formVariant,
      formVersionId: fields.formVersionId,
      franchiseNumber: fields.organizationNumber.toString(),
      recipient: fields.careFor,
      result,
      selectedOptions: fields.careOptions,
      spamBlockReason,
      surface: fields.surface,
      tags,
      validationErrors,
    }),
  );
}

async function trackSuccess(
  fields: SubmissionEventFields,
  answers: LeadFlowAnswers,
  leadId: string,
  tags: BotIdTags,
) {
  trackSubmission(fields, "success", tags);

  await identifyLead({
    email: answers.email ?? "",
    firstName: answers.firstName ?? "",
    lastName: answers.lastName ?? "",
    phone: answers.phone ?? "",
  });

  trackLeadCreated({
    formVariant: fields.formVariant,
    formVersionId: fields.formVersionId,
    leadId,
    organizationNumber: fields.organizationNumber,
    recipient: fields.careFor,
    selectedOptions: fields.careOptions,
    surface: fields.surface,
    tags,
  });
}

/**
 * The legacy submission events for a lead flow: `service-lead-form-submit`
 * for every outcome, plus `identifyLead` and `sf-lead-created` on success.
 * As with lead forms, the flow's `versionId` is sent as the form variant.
 *
 * Each response is tracked once. The flow stays mounted, so a later
 * submission's response is a new state object and is tracked again, while
 * re-renders of the same state are not.
 */
function useSubmissionTracking({
  officeSubmission,
  state,
  submittedAnswers,
  surface,
  versionId,
}: UseSubmissionTrackingInput): void {
  const trackedState = useRef<LeadFlowResponse | null | undefined>(undefined);

  useEffect(() => {
    if (trackedState.current === state) {
      return;
    }
    trackedState.current = state;

    if (state === null) {
      return;
    }

    const answers = submittedAnswers ?? NO_ANSWERS;
    const fields: SubmissionEventFields = {
      careFor: answers.careFor ?? "",
      careOptions: getSelectedCareTypes(answers),
      formVariant: versionId as FormVariant,
      formVersionId: versionId,
      organizationNumber:
        officeSubmission?.organizationNumber ?? answers.organizationNumber ?? 0,
      surface,
    };

    if (!state.ok) {
      if (state.validationErrors) {
        trackSubmission(
          fields,
          "validation-error",
          state.tags,
          state.validationErrors,
          state.spamBlockReason,
        );
      } else {
        trackSubmission(fields, "server-error", state.tags);
      }
      return;
    }

    if (state.data?.isError) {
      const errors = state.data.errors ?? [];
      const isDuplicate = errors.some((error) => error.code === "duplicate");
      if (!isDuplicate) {
        console.error({
          error: errors,
          message: "LeadFlow - Submission Error: server-error",
        });
      }
      trackSubmission(
        fields,
        isDuplicate ? "duplicate" : "server-error",
        state.tags,
      );
      return;
    }

    const leadId = state.data?.id;
    if (leadId) {
      void trackSuccess(fields, answers, leadId, state.tags);
    }
  }, [officeSubmission, state, submittedAnswers, surface, versionId]);
}

export { useSubmissionTracking };
